import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzFormModule } from 'ng-zorro-antd/form';
import { FormFieldConfigType } from '../../models';
import { LocalizationPipe } from '@shared/modules/localization';

@Component({
  selector: 'ui-form-checkbox',
  templateUrl: './form-checkbox.component.html',
  styleUrl: './form-checkbox.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [NzFormModule, NzCheckboxModule, FormsModule, LocalizationPipe],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UIFormCheckbox),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => UIFormCheckbox),
      multi: true,
    },
  ],
})
export class UIFormCheckbox implements ControlValueAccessor, Validator {
  @Input() set config(configValue: FormFieldConfigType) {
    this.label = configValue.label;
    this.link = configValue.link;
  }
  @Input() fieldName?: string;
  @Input() label?: string;
  @Input() link?: string;
  @Input() disabled = false;
  @Input() value = false;

  @Output() linkClicked = new EventEmitter<void>();

  control?: AbstractControl;
  errors?: ValidationErrors | null;

  onBlur(): void {
    this.onTouched();
  }

  onValueChange(isChecked: boolean): void {
    this.value = isChecked;
    this.onChange(this.value);
    this.updateControlValidity(); // Ensure control state is updated
  }

  protected onValidatorChange: () => void = () => void {};
  protected onChange: (value: boolean) => void = () => void {};
  protected onTouched: () => void = () => void {};

  // ControlValueAccessor methods
  writeValue(value: boolean): void {
    this.value = !!value;
    this.updateControlValidity(); // Ensure control state is updated
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Validator method
  validate(control: AbstractControl): ValidationErrors | null {
    this.control = control; // Keep a reference to the control
    return control.errors; // Return the errors from the control itself
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn; // Store the callback function
  }

  protected updateControlValidity(): void {
    if (this.control) {
      this.control.setErrors(null);
      this.control.updateValueAndValidity({ emitEvent: false });
    }
    // Notify Angular about the validator change
    this.onValidatorChange();
  }
}
